/**
 * ManualAssessmentSection Component
 * Wraps manual assessment history and task form with tabs
 */

import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ManualAssessmentForm, type ManualAssessmentFormProps } from "./ManualAssessmentForm";
import { ManualAssessmentHistory } from "./ImpactAssessment/ManualAssessmentHistory";

interface ManualAssessmentSectionProps extends ManualAssessmentFormProps {
  regulationId: string;
  regulationVersion: string;
  /** When false, the Task Form tab is hidden (e.g. workflow not completed) */
  showTaskForm?: boolean;
  /** Default active tab id: "history" or "taskForm" */
  defaultTab?: "history" | "taskForm";
  onRefreshHistory?: () => void;
  /** Currently open half-page P&T URI (null when the pane is closed). */
  activeWholeWorkflowUrl?: string | null;
}

export const ManualAssessmentSection: React.FC<ManualAssessmentSectionProps> = (props) => {
  const {
    regulationId,
    regulationVersion,
    showTaskForm = true,
    defaultTab = "history",
    onRefreshHistory,
    onOpenWholeWorkflow,
    activeWholeWorkflowUrl,
    ...formProps
  } = props;

  const tabs = useMemo(() => {
    const list: Array<{ id: string; label: string }> = [
      { id: "history", label: "Assessment History" },
    ];
    if (showTaskForm) {
      list.push({ id: "taskForm", label: "Task Form" });
    }
    return list;
  }, [showTaskForm]);

  const [activeTab, setActiveTab] = useState(0);
  const [hasInitializedTab, setHasInitializedTab] = useState(false);

  React.useEffect(() => {
    if (!props.loading && !hasInitializedTab) {
      if (defaultTab === "taskForm" && showTaskForm) {
        setActiveTab(1);
      } else {
        setActiveTab(0);
      }
      setHasInitializedTab(true);
    }
  }, [props.loading, hasInitializedTab, defaultTab, showTaskForm]);

  /** When regulation or version changes, reset initialization flag so tab can re-sync */
  React.useEffect(() => {
    setHasInitializedTab(false);
  }, [regulationId, regulationVersion]);

  const [refreshKey, setRefreshKey] = useState(0);

  const currentTabId = tabs[activeTab]?.id ?? "history";

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    onRefreshHistory?.();
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "column", mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Manual Impact Assessment
        </Typography>
      </Box>

      <Box>
        {/* Tabs and Refresh Button Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider",
            mb: 3,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                minWidth: 100,
                fontWeight: 500,
                color: "#6b7280",
                "&.Mui-selected": {
                  color: "primary.main",
                  fontWeight: 600,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "primary.main",
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.id} label={tab.label} />
            ))}
          </Tabs>

          <Tooltip title="Refresh">
            <IconButton
              onClick={handleRefresh}
              size="small"
              sx={{
                color: "#6b7280",
                mb: 0.5,
                "&:hover": {
                  backgroundColor: "#f3f4f6",
                  color: "text.primary",
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ mt: 2 }}>
          {/* Keep both panels mounted so Task Form draft/saved state survives tab switches. */}
          <Box
            sx={{ display: currentTabId === "history" ? "block" : "none" }}
            aria-hidden={currentTabId !== "history"}
          >
            <ManualAssessmentHistory
              regulationId={regulationId}
              regulationVersion={regulationVersion}
              key={`history-${refreshKey}`}
              onRefreshNeeded={onRefreshHistory}
              onOpenWholeWorkflow={onOpenWholeWorkflow}
              activeWholeWorkflowUrl={activeWholeWorkflowUrl}
            />
          </Box>
          {showTaskForm && (
            <Box
              sx={{ display: currentTabId === "taskForm" ? "block" : "none" }}
              aria-hidden={currentTabId !== "taskForm"}
            >
              <ManualAssessmentForm
                {...formProps}
                onOpenWholeWorkflow={onOpenWholeWorkflow}
                key={`form-${refreshKey}`}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
